import anim from '~/assets/anim.riv'
import statImg1 from '~/assets/stat/1.svg'
import statImg2 from '~/assets/stat/2.svg'
import statImg3 from '~/assets/stat/3.svg'
import statImg4 from '~/assets/stat/4.svg'

import { Fit, Layout, Rive } from '@rive-app/canvas'
import {
  createEffect,
  createSignal,
  For,
  onCleanup,
  onMount,
  Show,
  type JSXElement,
} from 'solid-js'
import { Motion } from 'solid-motionone'
import { NextIcon } from '~/components/icon/next'
import { Dialog, DialogContent } from '~/components/ui/dialog'
import { cn } from '~/lib/utils'
import { m } from '~/paraglide/messages'
import { ForestIcon } from '../icon/forest'
import { TffLogoIcon } from '../icon/tff-logo'
import { configByStep, resultNumbersByPlan } from './config'

export function StatsFlow(props: {
  plan: number
  isOpen: boolean
  close: () => void
}) {
  return (
    <Dialog
      open={props.isOpen}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          props.close()
        }
      }}
    >
      <DialogContent class={'h-full w-full'}>
        <Step close={props.close} plan={props.plan} />
      </DialogContent>
    </Dialog>
  )
}

function Step(props: { close: () => void; plan: number }) {
  const [currentStep, setCurrentStep] = createSignal(1)
  const [canNext, setCanNext] = createSignal(false)

  const currentConfig = () =>
    configByStep[currentStep() as keyof typeof configByStep]

  return (
    <div
      class={cn(
        'relative flex h-full w-full flex-col px-6 py-12 transition-colors',
        currentConfig().classes,
      )}
    >
      {/* Top absolute bg image */}
      <Show when={currentStep() !== 5}>
        <div class={cn('absolute left-0 right-0 top-0 h-[531px] w-full')}>
          <div
            class="absolute inset-0 bottom-0 left-1/2 top-0 flex h-[480px] w-[1160px] -translate-x-1/2 justify-center opacity-60"
            style={{
              mask: 'radial-gradient(55% 55% at 50% 50%, black 62%, transparent 94%)',
              '-webkit-mask':
                'radial-gradient(55% 55% at 50% 50%, black 62%, transparent 94%)',
            }}
          >
            <img
              class="h-full w-screen object-cover"
              src={currentConfig().bgImg}
            />
          </div>
        </div>
      </Show>

      {/* Main section */}
      <div class="relative mx-auto flex h-full max-w-[640px] flex-col justify-center">
        <Show
          when={currentStep() === 5}
          fallback={
            <GeneralContent
              onReady={() => setCanNext(true)}
              plan={props.plan}
              step={currentStep()}
              title={currentConfig().titleFn({
                number: String(
                  currentConfig().titleNumberByPlan[
                    props.plan as 1 | 2 | 3 | 4
                  ],
                ),
              })}
              desc={currentConfig().descFn({
                number: String(
                  currentConfig().descNumberByPlan[props.plan as 1 | 2 | 3 | 4],
                ),
              })}
            />
          }
        >
          <StatsCard plan={props.plan} onReady={() => setCanNext(true)} />
        </Show>
      </div>

      {/* Bottom section */}
      <div class="mx-auto flex w-full max-w-[640px] justify-center">
        <Show
          when={currentStep() === 5}
          keyed
          fallback={
            <For each={Array.from({ length: 4 })}>
              {(_, index) => (
                <Show when={index() === currentStep() - 1} keyed>
                  <Motion.div
                    initial={{ opacity: 0 }}
                    animate={{
                      opacity: 1,
                      transition: { duration: 0.5, delay: 2 },
                    }}
                    class="w-full"
                  >
                    <NextButton
                      class={currentConfig().btnClass}
                      disabled={!canNext()}
                      onClick={() => {
                        setCanNext(false)
                        setCurrentStep((step) => step + 1)
                      }}
                    >
                      {currentStep()} / 5
                    </NextButton>
                  </Motion.div>
                </Show>
              )}
            </For>
          }
        >
          <HeartButton onClick={props.close} disabled={!canNext()}>
            {m.earth_day_2025_impact_conclude_btn()}
          </HeartButton>
        </Show>
      </div>
    </div>
  )
}

function GeneralContent(props: {
  title: string
  desc: string
  step: number
  plan: number
  onReady: () => void
}) {
  return (
    <div class="space-y-3 transition-opacity duration-500">
      <div class="mx-auto size-[180px]">
        <StatItemAnim
          step={props.step}
          plan={props.plan}
          ready={props.onReady}
        />
      </div>
      <div class="space-y-3 text-center text-lg font-bold">
        <p class="whitespace-pre-line text-white">{props.title}</p>
        <p class="whitespace-pre-line">{props.desc}</p>
      </div>
    </div>
  )
}

function StatsCard(props: { plan: number; onReady: () => void }) {
  const numbers = () => resultNumbersByPlan[props.plan]
  return (
    <div
      class="relative rounded-[20px] px-5 pb-3 pt-[200px] transition-all duration-500"
      style={{
        'box-shadow': '0 0 20px 0 rgba(255, 251, 251, 0.40)',
        background: 'linear-gradient(180deg, #FFCDCD 19.67%, #FFF2F2 47.6%)',
      }}
    >
      <div class="absolute left-1/2 right-0 top-[-114px] size-[300px] -translate-x-1/2">
        <StatItemAnim step={5} plan={props.plan} ready={props.onReady} />
      </div>
      <div class="grid grid-cols-2 grid-rows-2 gap-[6px] md:grid-cols-4 md:grid-rows-1">
        <StatItem img={statImg1}>+{numbers().tree}</StatItem>
        <StatItem img={statImg2}>-{numbers().co2} kg</StatItem>
        <StatItem img={statImg3}>+{numbers().land} m²</StatItem>
        <StatItem img={statImg4}>+{numbers().water} L</StatItem>
      </div>

      <div class="mt-4 grid h-[60px] place-items-center text-center text-sm">
        <p class="text-[#E25C5C]">{m.earth_day_2025_impact_conclude_title()}</p>
      </div>

      <div class="mt-6 flex h-6 shrink-0 items-center justify-center gap-3 text-center">
        <ForestIcon />
        <svg
          width="2"
          height="17"
          viewBox="0 0 2 17"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            opacity="0.41"
            d="M0.875 0.875V15.875"
            stroke="#FFA195"
            stroke-width="1.75"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        <TffLogoIcon />
      </div>
    </div>
  )
}

function StatItem(props: { children: JSXElement; img: string }) {
  return (
    <div class="flex h-[42px] items-center space-x-[3px] rounded-xl bg-white px-[9px]">
      <div class="size-[30px]">
        <img class="max-w-full" src={props.img} />
      </div>
      <span class="text-[15px] font-bold text-[#9D3C33]">{props.children}</span>
    </div>
  )
}

function NextButton(props: {
  children: JSXElement
  onClick: () => void
  disabled: boolean
  class?: string
}) {
  return (
    <button
      class={cn(
        'flex h-[50px] w-full items-center justify-center space-x-2 rounded-xl border-2 border-pink-500 bg-pink-500 font-bold text-pink-500 shadow-[0px_6px_#C4C1C1] active:translate-y-[6px] active:shadow-none',
        props.class,
      )}
      onClick={props.onClick}
      disabled={props.disabled}
    >
      <span>{props.children}</span>
      <NextIcon />
    </button>
  )
}

function HeartButton(props: {
  children: JSXElement
  onClick: () => void
  disabled: boolean
}) {
  return (
    <button
      class="flex h-[50px] w-full min-w-[180px] items-center justify-center space-x-2 rounded-xl border-2 border-[#DB7F79] bg-[#FFF2F2] font-bold text-[#9D3C33] shadow-[0px_6px_#DB7F79] active:translate-y-[6px] active:shadow-none"
      onClick={props.onClick}
      disabled={props.disabled}
    >
      <span>{props.children}</span>
    </button>
  )
}

function StatItemAnim(props: {
  step: number
  plan: number
  ready: () => void
}) {
  let canvas!: HTMLCanvasElement
  let rive: Rive
  onMount(() => {
    rive = new Rive({
      canvas,
      src: anim,
      artboard: props.step === 5 ? 'reward_value_love' : 'reward_value',
      stateMachines: ['State Machine 1'],
      layout: new Layout({ fit: Fit.Contain }),
      onLoad: () => {
        rive.resizeDrawingSurfaceToCanvas()
        const inputs = rive.stateMachineInputs('State Machine 1')
        if (props.step === 5) {
          const inputs = rive.stateMachineInputs('State Machine 1')
          const triggerInput = inputs.find((input) => input.name === 'active')
          triggerInput?.fire()
        } else {
          const valueInput = inputs.find((input) => input.name === 'value')
          if (valueInput) {
            valueInput.value = props.plan
          }
        }
        rive.play()
      },
      onStateChange: (state) => {
        if (typeof state.data === 'object') {
          if ((state.data as string[])[0]?.includes('idle')) {
            props.ready()
          }
        }
      },
    })
  })
  onCleanup(() => {
    rive?.cleanup()
  })
  createEffect(() => {
    if (props.step > 1 && props.step < 5) {
      const inputs = rive?.stateMachineInputs('State Machine 1') ?? []
      const triggerInput = inputs.find((input) => input.name === 'active')
      triggerInput?.fire()
    }
  })
  return <canvas class="h-full w-full" ref={canvas} />
}
